/* eslint-disable @typescript-eslint/restrict-plus-operands */
/* eslint-disable no-unsafe-optional-chaining */
import { PageOptions } from '@graphcommerce/framer-next-pages'
import { ApolloError, useMutation, useQuery } from '@graphcommerce/graphql'
import { useCustomerQuery } from '@graphcommerce/magento-customer'
import { CountryRegionsDocument, StoreConfigDocument } from '@graphcommerce/magento-store'
import { PageMeta, filterNonNullableKeys } from '@graphcommerce/next-ui'
// eslint-disable-next-line import/no-extraneous-dependencies
import { useForm } from '@graphcommerce/react-hook-form'
import { i18n } from '@lingui/core'
import { Trans } from '@lingui/react'
import { Autocomplete, Checkbox, MenuItem, Paper, Popper, TextField } from '@mui/material'
import { GetStaticProps } from 'next'
import { ChangeEvent, SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react'
import Confetti from 'react-confetti'
import { LayoutDocument, LayoutNavigation, LayoutNavigationProps } from '../../components'
import {
  handleAddEmailToNewsletterList,
  handleGetKlaviyoProfileDetails,
  handleSubscribeToNewsletter,
  handleUnsubscribeProfileFromList,
} from '../../components/BuilderIO/KlaviyoNewsletterApiCall'
import { PrimaryButton } from '../../components/Button'
import { SectionTitle } from '../../components/Container/SectionTitle'
import { StandardDropdown } from '../../components/Dropdown/StandardDropdown'
import { AlertPrompt } from '../../components/Prompt/AlertPrompt'
import { ErrorPrompt } from '../../components/Prompt/ErrorPrompt'
import { SendReferralSignUpEmailDocument } from '../../components/SendReferralSignUpEmail/SendReferralSignUpEmail.gql'
import { StandardTextField } from '../../components/TextInput/TextFieldStyles'
import { CustomerDocument } from '../../graphql/Customer.gql'
import { useGoogleAddressAutocomplete } from '../../hooks/useGoogleAddressAutocomplete'
import { graphqlSharedClient, graphqlSsrClient } from '../../lib/graphql/graphqlSsrClient'

type GetPageStaticProps = GetStaticProps<LayoutNavigationProps>

const CustomPopper = (props) => (
  <Popper
    {...props}
    placement='bottom-start'
    sx={{
      width: 'fit-content',
    }}
  />
)

export function ReferralPage() {
  const dashboard = useCustomerQuery(CustomerDocument, {
    fetchPolicy: 'cache-and-network',
  })
  const customer = dashboard.data?.customer
  const customerIndustry = customer?.company_industry

  const inputRef = useRef<HTMLInputElement>(null)
  const [firstName, setFirstName] = useState<string>()
  const [lastName, setLastName] = useState<string>()
  const [email, setEmail] = useState<string>()
  const [chosenValue, setChosenValue] = useState('#')
  const [currentStreet, setCurrentStreet] = useState('')
  const [inputStreet, setInputStreet] = useState('')
  const [city, setCity] = useState('')
  const [regionId, setRegionId] = useState<number>()
  const [regionCode, setRegionCode] = useState<string>()
  const [street, setStreet] = useState<string>()
  const [street2, setStreet2] = useState<string>()
  const [postCode, setPostCode] = useState<string>()
  const [company, setCompany] = useState<string>('')
  const [phoneNumber, setPhoneNumber] = useState<string>('')
  const [tin, setTin] = useState<string>('')

  const [isSubscribing, setIsSubscribing] = useState<boolean>(false)
  const [displayPrompt, setDisplayPrompt] = useState<boolean>(false)
  const [customError, setCustomError] = useState<Error>()
  const [showConfetti, setShowConfetti] = useState<boolean>()
  const [susbcribeToNewsletter, setSubscribeToNewsletter] = useState<boolean>(true)

  const formRef = useRef(null)
  const firstNameRef = useRef(null)
  const form = useForm()
  const { formState, register, handleSubmit } = form

  const [sendReferralSignUpEmail] = useMutation(SendReferralSignUpEmailDocument)

  const handleError = (e: Error) => {
    setCustomError(
      new Error(
        'We encountered an error while processing your subscription. We will get this issue fixed ASAP.',
      ),
    )
    console.log(e)
  }
  const { placePredictions, getPlaceDetails } = useGoogleAddressAutocomplete({
    // inputValue: chosenValue === inputRef.current?.value ? '' : inputRef.current?.value,
    inputValue: currentStreet,
    enabled: true,
  })

  const countryQuery = useQuery(CountryRegionsDocument, { fetchPolicy: 'cache-and-network' })
  const countries = countryQuery.data?.countries ?? countryQuery.previousData?.countries

  // const country = watch('countryCode')
  // const regionId = watch('regionId')

  const countryList = useMemo(() => {
    const countriesWithLocale = (countries ?? [])?.filter((c) => c?.full_name_locale)
    return countriesWithLocale.sort((a, b) =>
      (a?.full_name_locale ?? '')?.localeCompare(b?.full_name_locale ?? ''),
    )
  }, [countries])

  const regionList = useMemo(() => {
    const availableRegions = Object.values(
      countryList.find((c) => c?.two_letter_abbreviation === 'US')?.available_regions ?? {},
    )
    type Region = (typeof availableRegions)[0]

    const compare: (a: Region, b: Region) => number = (a, b) =>
      (a?.name ?? '')?.localeCompare(b?.name ?? '')

    return availableRegions?.sort(compare)
  }, [countryList])

  const regionOptions = filterNonNullableKeys(regionList, ['id', 'name']).map(
    ({ id, name: label }) => ({ value: Number(id), label }),
  )

  const menuItems = regionOptions.map((option) => (
    <MenuItem value={option.value} className='flex w-full items-center justify-between'>
      <span>{option.label}</span>
    </MenuItem>
  ))

  const handlePlaceSelect = (place: any) => {
    if (place.city) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      setCity(place.city)
    }
    if (place.regionCode) {
      const regionDetails = regionList.find((r) => r?.code === place.regionCode)
      if (regionDetails?.id) {
        setRegionId(regionDetails?.id)
        setRegionCode(regionDetails?.code ?? '')
      }
    }
    if (place.street) {
      setStreet([place.house || '', place.street || ''].join(' '))
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      setCurrentStreet([place.house || '', place.street || ''].join(' '))
      setInputStreet([place.house || '', place.street || ''].join(' '))
    }
    if (place.postCode) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      setPostCode(place.postCode)
    }
  }

  const handleChangeStreet = (place) => {
    getPlaceDetails(place.place_id, (placeDetails) => {
      handlePlaceSelect(placeDetails)
      setChosenValue(inputRef.current?.value || '')
    })
  }

  const placeholder = '555-555-5555'

  const setPhoneValue = (event: any) => {
    const newValue = event?.target?.value.replace(/\D/g, '')

    const valArry = newValue.replaceAll('-', '').trim().split('')

    const isDeleting = phoneNumber && phoneNumber?.length > event?.target?.value.length
    if (valArry) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      let val = ''
      const maxIter = valArry.length >= 10 ? 10 : valArry.length
      if (valArry.length >= 3) {
        for (let i = 0; i < maxIter; i++) {
          if (!isDeleting && (i === 2 || i === 5)) {
            val += `${valArry[i]}-`
          } else if (
            (isDeleting && i === 2 && valArry.length > 3) ||
            (i === 5 && valArry.length > 6)
          ) {
            val += `${valArry[i]}-`
          } else {
            val += valArry[i]
          }
        }
        setPhoneNumber(val)
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        setPhoneNumber(newValue)
      }
    }
  }

  const setTaxNumberValue = (event: any) => {
    const newValue = event?.target?.value.replace(/\D/g, '')

    const valArry = newValue.replaceAll('-', '').trim().split('')

    const isDeleting = phoneNumber && phoneNumber?.length > event?.target?.value.length
    if (valArry) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      let val = ''
      const maxIter = valArry.length >= 9 ? 9 : valArry.length
      if (valArry.length >= 3) {
        for (let i = 0; i < maxIter; i++) {
          if (!isDeleting && (i === 2 || i === 4)) {
            val += `${valArry[i]}-`
          } else if (
            (isDeleting && i === 2 && valArry.length > 3) ||
            (i === 4 && valArry.length > 5)
          ) {
            val += `${valArry[i]}-`
          } else {
            val += valArry[i]
          }
        }
        setTin(val)
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        setTin(newValue)
      }
    }
  }

  const clearFields = () => {
    setFirstName('')
    setLastName('')
    setEmail('')
    setCompany('')
    setTin('')
    setStreet('')
    setStreet2('')
    setCity('')
    setPostCode('')
    setPhoneNumber('')
    setRegionId(-1)
  }

  const submitHandler = handleSubmit(async () => {
    const result = await sendReferralSignUpEmail({
      variables: {
        address_1: street,
        address_2: street2,
        city,
        company_name: company,
        email: email ?? '',
        first_name: firstName ?? '',
        last_name: lastName ?? '',
        postal_code: postCode,
        state: regionCode,
        tax_id: tin,
        telephone: phoneNumber,
      },
    })

    if (result?.data?.sendReferralSignUpEmail) {
      const industries: Array<string> = susbcribeToNewsletter
        ? ['friends_of_templi', customerIndustry ?? 'guest']
        : ['friends_of_templi']

      /** Check if user exists in Klaviyo, if yes, add Fot * */
      const profileResult = await handleGetKlaviyoProfileDetails(handleError, email ?? '')
      if (profileResult?.data?.length > 0 && profileResult?.data?.at(0).id) {
        /* susbcribe to new list */
        await handleAddEmailToNewsletterList(
          setIsSubscribing,
          setDisplayPrompt,
          handleError,
          email,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          profileResult?.data?.at(0).id,
          ['friends_of_templi'],
        )
      } else {
        await handleSubscribeToNewsletter(
          setIsSubscribing,
          setDisplayPrompt,
          handleError,
          email ?? '',
          firstName ?? '',
          lastName ?? '',
          industries,
          true,
          { subscribed_from_referral_sign_up: true },
        ).then(() => {
          setShowConfetti(true)
          clearFields()
          setTimeout(() => setShowConfetti(false), 5000)
        })
      }
    } else {
      handleError(new Error('Error encountered whlile sending email.'))
    }
  })

  useEffect(() => {
    if (customer) {
      setFirstName(customer?.firstname ?? '')
      setLastName(customer?.lastname ?? '')
      setEmail(customer?.email ?? '')
    }
  }, [customer])

  return (
    <form
      onSubmit={submitHandler}
      noValidate
      className='flex items-center justify-center '
      ref={formRef}
    >
      {showConfetti && (
        <Confetti
          width={window?.innerWidth - 20}
          height={(formRef?.current as any).scrollHeight + 60}
          recycle={false}
          numberOfPieces={1000}
          className='z-100'
        />
      )}
      <div className='mx-[40px] my-[60px] shadow-xl md:mx-[10%] lg:mx-[22%]'>
        <PageMeta title={i18n._('Friends of Templi Sign Up')} metaRobots={['noindex']} />
        <Paper
          elevation={3}
          className='flex max-w-[800px] flex-col items-center justify-center p-10 md:items-start md:px-9 md:py-8'
        >
          <SectionTitle
            withHorizontalDivider
            title={i18n._('Friends of Templi Sign Up Form')}
            className='mb-5 w-full'
            withNoBottomMargin
          />
          <p className='Type-XL-Regular px-3 italic text-70-grey'>
            Fill out this form to join our affiliate referral program, Friends of Templi. When you
            introduce a business to Templi, you will receive 3% cash back for all product purchases
            made by that business. We do not include sales tax and shipping costs as part of the
            product purchase amount. Payments are mailed by check 4 times per year, at the
            conclusion of the financial quarter. Checks will be mailed to the address you provide in
            this form. To make an introduction, you will email{' '}
            <a
              href='mailto:contact@templi.com'
              className='text-material-ui-blue hover:text-material-ui-dark-blue hover:underline'
            >
              contact@templi.com
            </a>{' '}
            along with the company representative.
          </p>
          <div className='flex w-full items-center justify-center'>
            <div className='mt-6 flex flex-col items-center justify-center gap-y-5 md:items-start md:justify-start'>
              <div className='flex w-full flex-col items-center justify-start gap-x-5 gap-y-5 md:flex-row'>
                <StandardTextField
                  label={<Trans id='First Name' />}
                  variant='outlined'
                  required
                  className='min-w-[260px]'
                  size='small'
                  sx={{
                    width: '260px',
                    minWidth: '260px',
                  }}
                  error={formState.isSubmitted && !!formState.errors.firstName}
                  value={firstName ?? ''}
                  disabled={formState.isSubmitting}
                  {...register('firstName', {
                    required: true,
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                    onChange: (e) => setFirstName(e.target.value),
                  })}
                  // FormHelperTextProps={{ className: 'mt-0 ' }}
                  // helperText={formState?.errors?.firstName?.message?.toString()}
                />
                <StandardTextField
                  label={<Trans id='Last Name' />}
                  variant='outlined'
                  required
                  className='min-w-[260px]'
                  size='small'
                  sx={{
                    width: '260px',
                    minWidth: '260px',
                  }}
                  value={lastName ?? ''}
                  disabled={formState.isSubmitting}
                  error={formState.isSubmitted && !!formState.errors.lastName}
                  {...register('lastName', {
                    required: true,
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                    onChange: (e) => setLastName(e.target.value),
                  })}
                  // FormHelperTextProps={{ className: 'mt-0 ' }}
                  // helperText={formState?.errors?.lastName?.message?.toString()}
                />
              </div>

              <StandardTextField
                label={<Trans id='Email' />}
                variant='outlined'
                error={formState.isSubmitted && !!formState.errors.email}
                color='primary'
                required
                autoComplete='company'
                className='w-[260px] max-w-[260px]'
                size='small'
                sx={{
                  width: '260px',
                  minWidth: '260px',
                }}
                type='email'
                value={email ?? ''}
                disabled={formState.isSubmitting}
                {...register('email', {
                  required: true,
                  pattern: {
                    value: /\S+@\S+\.\S+/,
                    message: 'Please enter a valid email address.',
                  },
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                  onChange: (e) => setEmail(e.target.value),
                })}
                // FormHelperTextProps={{ className: 'mt-0 ' }}
                // helperText={formState?.errors?.email?.message?.toString()}
              />
              {/* <FormControl>
            <span className='text-70-grey'>Are you with an agency or an independent designer?</span>
            <RadioGroup
              aria-labelledby='demo-radio-buttons-group-label'
              defaultValue='female'
              name='radio-buttons-group'
            >
              <FormControlLabel
                sx={{ height: '30px' }}
                value='agency'
                control={
                  <Radio
                    sx={{
                      '&.Mui-checked': {
                        color: '#26942A',
                      },
                    }}
                  />
                }
                label='Agency'
                className='Type-XL-Regular whitespace-nowrap'
              />
              <FormControlLabel
                sx={{ height: '30px' }}
                value='independent'
                control={
                  <Radio
                    sx={{
                      '&.Mui-checked': {
                        color: '#26942A',
                      },
                    }}
                  />
                }
                label='Independent'
                className='Type-XL-Regular whitespace-nowrap'
              />
            </RadioGroup>
          </FormControl> */}
              <div className='flex w-full flex-col items-center justify-start gap-x-5 gap-y-5 md:flex-row'>
                <StandardTextField
                  label={<Trans id='Company Name' />}
                  variant='outlined'
                  color='primary'
                  className='w-[260px] max-w-[260px]'
                  size='small'
                  sx={{
                    width: '260px',
                    minWidth: '260px',
                  }}
                  disabled={formState.isSubmitting}
                  value={company}
                  onChange={(e) => setCompany(e?.target?.value)}
                />
                <StandardTextField
                  label={<Trans id='Tax ID' />}
                  variant='outlined'
                  color='primary'
                  className='w-[260px] max-w-[260px]'
                  size='small'
                  sx={{
                    width: '260px',
                    minWidth: '260px',
                  }}
                  value={tin}
                  disabled={formState.isSubmitting}
                  onKeyDown={(evt) => {
                    if (evt.keyCode < 48 || evt.keyCode > 57) {
                      if (
                        evt.key !== 'Backspace' &&
                        evt.key !== 'ArrowLeft' &&
                        evt.key !== 'ArrowRight'
                      )
                        evt.preventDefault()
                    } else if (phoneNumber && phoneNumber.length >= 12) {
                      evt.preventDefault()
                    }
                  }}
                  onChange={(e) => setTaxNumberValue(e)}
                />
              </div>
              <span className='text-70-grey'>Mailing Address:</span>

              <div className='flex w-full flex-col items-center justify-start gap-x-5 gap-y-5 md:flex-row'>
                <Autocomplete
                  fullWidth
                  freeSolo
                  size='small'
                  defaultValue={currentStreet ?? street}
                  value={street ?? currentStreet}
                  disabled={formState.isSubmitting}
                  inputValue={inputStreet}
                  options={placePredictions.map((place) => place?.description)}
                  componentsProps={{ popper: { style: { width: 'fit-content' } } }}
                  PopperComponent={CustomPopper}
                  onChange={(event: SyntheticEvent<Element, Event>, newValue: string) => {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                    const place = placePredictions.find((p) => p.description === newValue)
                    if (place && place?.place_id) {
                      getPlaceDetails(place.place_id, (placeDetails) => {
                        handlePlaceSelect(placeDetails)
                      })
                    }
                  }}
                  onInputChange={(event: SyntheticEvent<Element, Event>, newInputValue: string) => {
                    setInputStreet(newInputValue)
                  }}
                  slotProps={{
                    paper: {
                      elevation: 3,
                      sx: {
                        '& .MuiAutocomplete-listbox': {
                          '& .MuiAutocomplete-option': {
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            fontSize: '12px',
                            width: '100%',
                            minWidth: '100%',
                            whiteSpace: 'nowrap',
                          },
                        },
                      },
                    },
                  }}
                  sx={{ maxWidth: '260px' }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={<Trans id='Address 1' />}
                      color='primary'
                      sx={{
                        width: '260px',
                        minWidth: '260px',
                        '& .MuiFormLabel-root': {
                          fontSize: '14px',
                        },
                      }}
                      value={currentStreet}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        setCurrentStreet(event?.target?.value)
                        setStreet(event?.target?.value)
                      }}
                      size='small'
                    />
                  )}
                />
                <StandardTextField
                  label={<Trans id='Address 2' />}
                  variant='outlined'
                  color='primary'
                  autoComplete='address-line2'
                  className='outline-material-ui-blue'
                  size='small'
                  id='houseNumber'
                  sx={{
                    width: '260px',
                    minWidth: '260px',
                  }}
                  value={street2}
                  disabled={formState.isSubmitting}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    setStreet2(event?.target?.value)
                  }}
                />
              </div>
              <div className='flex w-full flex-col items-center justify-start gap-x-5 gap-y-5 md:flex-row'>
                <StandardTextField
                  label={<Trans id='City' />}
                  variant='outlined'
                  // error={!!formState.errors.city?.message}
                  color='primary'
                  className='outline-material-ui-blue'
                  size='small'
                  id='city'
                  sx={{
                    width: '260px',
                    minWidth: '260px',
                    maxWidth: '260px',
                  }}
                  value={city ?? ''}
                  onChange={(e) => setCity(e?.target?.value)}
                  disabled={formState.isSubmitting}
                />
                {regionList.length > 0 && (
                  <StandardDropdown
                    label='State'
                    onChange={(option) => {
                      if (option?.target?.value)
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                        setRegionId(option?.target?.value)
                      const regionDetails = regionList.find(
                        (r) => r?.id === option?.target?.value?.toString(),
                      )
                      if (regionDetails?.id) {
                        setRegionId(regionDetails?.id)
                        setRegionCode(regionDetails?.code ?? '')
                      }
                    }}
                    options={menuItems}
                    sx={{
                      width: '260px',
                      minWidth: '260px',
                    }}
                    value={regionId?.toString() ?? ''}
                    disabled={formState.isSubmitting}
                  />
                )}
              </div>
              <div className='flex w-full flex-col items-center justify-start gap-x-5 gap-y-5 md:flex-row'>
                <div className='w-[260px] max-w-[260px]' id='postCodeFormRow'>
                  <StandardTextField
                    label={<Trans id='Postal code' />}
                    variant='outlined'
                    color='primary'
                    className='outline-material-ui-blue'
                    size='small'
                    sx={{
                      width: '260px',
                      minWidth: '260px',
                    }}
                    value={postCode ?? ''}
                    onChange={(e) => setPostCode(e?.target?.value)}
                    defaultValue={postCode}
                    disabled={formState.isSubmitting}
                  />
                </div>
                <div>
                  <StandardTextField
                    placeholder={placeholder}
                    id='telephoneTextField'
                    type='text'
                    label={<Trans id='Telephone' />}
                    variant='outlined'
                    color='primary'
                    autoComplete='company'
                    className='w-[260px] max-w-[260px]'
                    size='small'
                    onKeyDown={(evt) => {
                      if (evt.keyCode < 48 || evt.keyCode > 57) {
                        if (
                          evt.key !== 'Backspace' &&
                          evt.key !== 'ArrowLeft' &&
                          evt.key !== 'ArrowRight'
                        )
                          evt.preventDefault()
                      } else if (phoneNumber && phoneNumber.length >= 12) {
                        evt.preventDefault()
                      }
                    }}
                    onChange={(e) => setPhoneValue(e)}
                    value={phoneNumber}
                    disabled={formState.isSubmitting}
                    sx={{
                      width: '260px',
                      minWidth: '260px',
                    }}
                  />
                </div>
              </div>
              <div className='flex w-full max-w-[260px] items-start justify-start gap-x-5 gap-y-5 md:max-w-[560px] md:flex-row'>
                <Checkbox
                  onClick={() => {
                    setSubscribeToNewsletter(!susbcribeToNewsletter)
                  }}
                  checked={susbcribeToNewsletter}
                  id='subscribeToNewsletter'
                  sx={{ padding: 0 }}
                />
                <label htmlFor='subscribeToNewsletter' className='text-[12px]'>
                  {i18n._(
                    "Also sign up to receive coupons, advice and inspiration. You can unsubscribe from email marketing via the 'Unsubscribe' link in the emails.",
                  )}
                </label>
              </div>
              <div className='mt-6 flex w-full flex-col items-center justify-center gap-[30px] md:flex-row md:justify-start'>
                <PrimaryButton
                  type='submit'
                  className='w-full max-w-[260px]  md:w-auto'
                  loading={formState.isSubmitting}
                  onSubmit={() => handleSubmit}
                >
                  <Trans id='submit' />
                </PrimaryButton>
              </div>
            </div>
          </div>
        </Paper>
        <ErrorPrompt error={customError as ApolloError} />
        <AlertPrompt
          title={i18n._('Friends of Templi Sign Up')}
          message={i18n._('Thank you for signing up for our Friends of Templi referral program!')}
          isDisplayed={displayPrompt}
          setIsDisplayed={setDisplayPrompt}
        />
      </div>
    </form>
  )
}

const pageOptions: PageOptions<LayoutNavigationProps> = {
  Layout: LayoutNavigation,
}
ReferralPage.pageOptions = pageOptions

export default ReferralPage

export const getStaticProps: GetPageStaticProps = async ({ locale }) => {
  const staticClient = graphqlSsrClient(locale)
  const client = graphqlSharedClient(locale)
  const conf = client.query({ query: StoreConfigDocument })
  const layout = staticClient.query({ query: LayoutDocument, fetchPolicy: 'cache-first' })

  return {
    props: {
      ...(await layout).data,
      up: { href: '/', title: 'Home' },
      apolloState: await conf.then(() => client.cache.extract()),
    },
    revalidate: process.env.SKIP_ISG ? 1 : 60 * 20,
  }
}
